import { useEffect, useState, useCallback, useReducer } from "react";
import { useCurrentUser, useCentralApi } from "hooks/shared";
import { AlertColor } from "@mui/material";

type WorkItemDraftDeleteResult = [
    (workItemId: number) => Promise<void>,
    boolean,
    string | null
];
type PostUpdateSignature = (data: any) => void;

const useWorkItemDraftDelete: (displaySnackbar?: (severity: AlertColor, text: string) => void, postUpdate?: PostUpdateSignature) => WorkItemDraftDeleteResult = (displaySnackbar, postUpdate) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const deleteDraft = useCallback(async (workItemId: number) => {
        if (!user || !user.id_token || !workItemId) return;

        const controller = new AbortController();
        setLoading(true);
        try {
            const resp = await fetcher(`/api/v3/drafts/workitems/${workItemId}/delete`, {
                method: 'PUT',
                signal: controller.signal
            });

            if (!resp.ok) {
                throw new Error('Failed to delete work item draft');
            }

            if (displaySnackbar) {
                displaySnackbar("success", "Success! Work item draft deleted");
            }

            if (postUpdate) {
                postUpdate(workItemId);
            }
        } catch (err: any) {
            setError(err.message);
            if (displaySnackbar) {
                displaySnackbar("error", "We couldn't delete the work item draft. Please try again");
            }
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [user, fetcher, displaySnackbar, postUpdate]);

    return [deleteDraft, loading, error];
};

export default useWorkItemDraftDelete;